import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { UseCreateHookFromSelectionArgs } from './types';
import useCreateHookSegment from '../hooks/useCreateHookSegment';
import { defaultTimelineSelectionState, transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { HOOK_BUTTON_WIDTH_IN_PX, HOOK_MIN_DURATION_IN_SECS } from '@/components/molecules/Transcript/constants';
import { getTimeWithoutDeletesForRange } from '@/components/molecules/Transcript/utils';

export default function useCreateHookFromSelection({
  clipData,
  clipId,
  startTime,
  endTime,
  startSegmentId,
  endSegmentId,
  isCreatingSelection,
  selectionContainerWidth
}: UseCreateHookFromSelectionArgs) {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isSplitsEnabled = Boolean(featureFlags[FeatureFlagKeys.Use_CL_Splits_In_Timeline]);
  const createHook = useCreateHookSegment();

  const handleCreateHookSegment = useCallback(() => {
    const { startTime, endTime, startSegmentId, endSegmentId } = transcriptTimelineSelectionStore.getSnapshot();
    if (startTime === null || endTime === null || startSegmentId === null || endSegmentId === null) {
      return;
    }

    createHook({ startTime, endTime, startSegmentId, endSegmentId, clipData, clipId });
    transcriptTimelineSelectionStore.set(() => ({ ...defaultTimelineSelectionState }));
  }, [clipData, clipId, createHook]);

  const isAddHookVisible = useMemo(() => {
    if (HOOK_BUTTON_WIDTH_IN_PX > selectionContainerWidth) {
      return false;
    }
    if (startTime === null || endTime === null || startSegmentId === null || endSegmentId === null) {
      return false;
    }
    if (!isSplitsEnabled) {
      return false;
    }
    if (isCreatingSelection) {
      return false;
    }
    if (startSegmentId !== endSegmentId) {
      return false;
    }
    const totalTimeWithoutDeletes = getTimeWithoutDeletesForRange({
      startTime,
      endTime,
      startSegmentId,
      endSegmentId,
      clipData
    });
    if (totalTimeWithoutDeletes === 0) {
      return false;
    }
    const duration = endTime - startTime;
    return duration >= HOOK_MIN_DURATION_IN_SECS;
  }, [
    clipData,
    endSegmentId,
    endTime,
    isCreatingSelection,
    isSplitsEnabled,
    selectionContainerWidth,
    startSegmentId,
    startTime
  ]);

  return {
    handleCreateHookSegment,
    isAddHookVisible
  };
}
